import React, { createContext, useState, useContext, useEffect, useCallback, useRef } from 'react';
import { getProfilePhoto } from '../api/services';
import { AuthContext } from './auth-context';

const ProfileContext = createContext();

export const useProfile = () => {
  const context = useContext(ProfileContext);
  if (!context) {
    throw new Error('useProfile must be used within a ProfileProvider');
  }
  return context;
};

export const ProfileProvider = ({ children }) => {
  const [profileImageUrl, setProfileImageUrl] = useState(null);
  const [loading, setLoading] = useState(true);
  const { user, isAuthenticated, token } = useContext(AuthContext);
  
  // Add this ref to track if we've already fetched
  const hasFetched = useRef(false);

  const fetchProfilePhoto = useCallback(async () => {
    if (!isAuthenticated || !token) {
      setLoading(false);
      return;
    }

    // Don't fetch if we've already fetched
    if (hasFetched.current) {
      setLoading(false);
      return;
    }

    try {
      const data = await getProfilePhoto();
      if (data && data.size > 0) {
        if (profileImageUrl) {
          URL.revokeObjectURL(profileImageUrl);
        }
        const newUrl = URL.createObjectURL(data);
        setProfileImageUrl(newUrl);
      } else {
        setProfileImageUrl(null);
      }
    } catch (error) {
      console.error('Error fetching profile photo for user:', user?.email || 'unknown', error);
      setProfileImageUrl(null);
    } finally {
      setLoading(false);
      hasFetched.current = true;
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, token, user?.email]);

  const updateProfileImage = async (file) => {
    // Reset the fetch flag when updating
    hasFetched.current = false;
    await fetchProfilePhoto();
  };

  useEffect(() => {
    if (isAuthenticated && token) {
      fetchProfilePhoto();
    } else {
      setProfileImageUrl(null);
      setLoading(false);
      hasFetched.current = false;
    }

    return () => {
      if (profileImageUrl) {
        URL.revokeObjectURL(profileImageUrl);
      }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, token]);

  return (
    <ProfileContext.Provider value={{ profileImageUrl, updateProfileImage, loading }}>
      {children}
    </ProfileContext.Provider>
  );
};